import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../../styles/tutor.css';
import '../../../styles/prepare-up-home.css';
import '../../../styles/prepare-up-theory.css';
import { getLearnerId } from '../../../lib/firebase';
import APIService from '../../../services/APIService';
import { IconArrowLeft, IconBook, IconMic, IconPerson } from '../icons';
import { renderMarkdown } from '../markdown';
import ModeEmpty from '../ModeEmpty';
import LearnPanel, { useLearnOpen } from '../LearnPanel';
import { buildPaths, getHubTreeCached } from '../paths';
import { HomeSearch, ThemeToggle, buildSearchEntries } from '../HomeDashboard';
import { scopeIds } from '../memoryLog';
import { resolve } from './registry';
import { startSession, sendEvent, sendSay } from './events';
import { makeEvent, noteMounted } from './events';

/*
 * TutorMode — the chatbot Theory Tutor shell. The agent (agentic_engine
 * LangGraph loop) drives ALL UI: it streams text_delta (chat bubbles) and
 * render/update frames (registry components). This shell only renders frames
 * and posts UIEvents back — no tutoring logic, grading, or state lives here.
 *
 * Layout: chat transcript (main/inline slots in-stream) + right side rail
 * ("side" slot: hints, progress) + toast layer + composer (free-text doubts).
 */
const TutorMode = ({
  config, onConfigure,
  onBack, profile, onEditProfile, theme, onToggleTheme, onOpenPath, onOpenHubNode,
}) => {
  const cids = useMemo(() => scopeIds(config), [config]);
  // Same top bar as Home: global search over the hub.
  const [hub, setHub] = useState(null);
  useEffect(() => {
    let live = true;
    getHubTreeCached().then((d) => { if (live) setHub(d.tree || []); }).catch(() => { if (live) setHub([]); });
    return () => { live = false; };
  }, []);
  const searchEntries = useMemo(() => {
    const p = hub ? buildPaths(hub) : null;
    return buildSearchEntries(hub, p ? [...p.roles, ...p.areas] : []);
  }, [hub]);
  const [learnOpen, toggleLearn] = useLearnOpen('theory');
  const [learnConcept, setLearnConcept] = useState(null);
  // The session SURVIVES leaving the tab: sid + transcript + components are
  // stored per scope and restored on return (a dead server session silently
  // restarts via the existing session_not_found path).
  const storeKey = useMemo(() => `nano_tutor_${cids.join('|')}`, [cids]);
  const clearStored = useCallback(() => {
    try { sessionStorage.removeItem(storeKey); } catch { /* ignore */ }
  }, [storeKey]);
  const [sessionId, setSessionId] = useState(null);
  const [transcript, setTranscript] = useState([]);   // [{kind:'text',id,md,streaming}|{kind:'component',cid}]
  const [components, setComponents] = useState({});   // cid -> {component, slot, question_id, props, status}
  const [toastId, setToastId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fatal, setFatal] = useState('');
  const [input, setInput] = useState('');
  const lastTextRef = useRef(null);                   // current streaming bubble id
  const restartingRef = useRef(false);
  const startedRef = useRef(false);                   // StrictMode double-mount guard
  const componentsRef = useRef({});                   // sync mirror for frame handling
  const endRef = useRef(null);

  const handleFrame = useCallback((frame) => {
    switch (frame.type) {
      case 'hello':
        setSessionId(frame.session_id);
        break;
      case 'text_delta': {
        setTranscript((prev) => {
          const cur = lastTextRef.current;
          const last = prev[prev.length - 1];
          if (cur && last && last.kind === 'text' && last.id === cur) {
            return [...prev.slice(0, -1), { ...last, md: last.md + frame.text }];
          }
          const id = `${frame.msg_id}-${prev.length}`;
          lastTextRef.current = id;
          return [...prev, { kind: 'text', id, md: frame.text, streaming: true }];
        });
        break;
      }
      case 'render': {
        const c = frame.call;
        if (c.component === 'progress_bar') break;     // endless practice — no progress bar UI
        const record = {
          component: c.component, slot: c.slot || 'main',
          question_id: c.question_id || null, props: c.props || {}, status: 'active',
        };
        componentsRef.current[c.component_id] = record;
        setComponents((prev) => ({ ...prev, [c.component_id]: record }));
        noteMounted(c.component_id);
        if (c.slot === 'toast') {
          setToastId(c.component_id);
        } else {
          // Everything (hint panels included) flows inline, right below its
          // question card — there is no side rail.
          lastTextRef.current = null;                  // in-flow component → next text is a new bubble
          setTranscript((prev) => [...prev, { kind: 'component', cid: c.component_id }]);
        }
        break;
      }
      case 'update': {
        const { component_id: cid, patch } = frame.call;
        setComponents((prev) => {
          const comp = prev[cid];
          if (!comp) return prev;
          const props = { ...comp.props };
          Object.entries(patch || {}).forEach(([k, v]) => {
            if ((k === 'unlocked' || k === 'steps_unlocked') && Array.isArray(v)) {
              const existing = props[k] || [];
              const seen = new Set(existing.map((x) => x.level ?? x.i));
              props[k] = [...existing, ...v.filter((x) => !seen.has(x.level ?? x.i))];
            } else {
              props[k] = v;
            }
          });
          componentsRef.current[cid] = { ...comp, props };
          return { ...prev, [cid]: componentsRef.current[cid] };
        });
        break;
      }
      case 'ack': {
        // The server grades BEFORE the LLM runs — surface the verdict on the
        // card instantly instead of making the learner wait for prose.
        if (frame.component_id) {
          setComponents((prev) => {
            const hit = prev[frame.component_id];
            if (!hit) return prev;
            const next = {
              ...prev,
              [frame.component_id]: { ...hit, status: 'submitted', verdict: frame.result || null },
            };
            // Retire the answered question's hint chip.
            Object.entries(prev).forEach(([id, comp]) => {
              if (comp.component === 'hint_panel' && comp.question_id
                  && comp.question_id === hit.question_id && comp.status === 'active') {
                next[id] = { ...comp, status: 'submitted' };
              }
            });
            componentsRef.current = next;
            return next;
          });
        }
        break;
      }
      case 'error': {
        if (frame.code === 'session_not_found' && !restartingRef.current) {
          restartingRef.current = true;
          try { sessionStorage.removeItem(`nano_tutor_${(config ? scopeIds(config) : []).join('|')}`); } catch { /* ignore */ }
          setTranscript((prev) => [...prev, {
            kind: 'text', id: `sys-${prev.length}`,
            md: '_Your previous session expired — starting a fresh one on the same topics (your progress is saved in memory)._',
          }]);
          begin();                                     // eslint-disable-line no-use-before-define
        } else if (frame.code !== 'busy') {
          setFatal(frame.message || 'The tutor hit an error.');
        }
        break;
      }
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = useCallback(async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { setFatal(e.message || 'Connection lost.'); }
    setBusy(false);
    lastTextRef.current = null;
    setTranscript((prev) => prev.map((t) => (t.streaming ? { ...t, streaming: false } : t)));
  }, []);

  const begin = useCallback(() => {
    restartingRef.current = false;
    run(() => startSession({
      learner_id: getLearnerId(),
      concept_ids: cids,
      options: config?.options || {},
      focus_areas: config?.plan?.focus_areas || [],
    }, { onFrame: handleFrame }));
  }, [cids, config, handleFrame, run]);

  // StrictMode mounts effects twice in dev — the ref guard prevents TWO
  // concurrent sessions streaming interleaved frames into one transcript.
  useEffect(() => {
    if (!cids.length || startedRef.current) return;
    startedRef.current = true;
    try {
      const saved = JSON.parse(sessionStorage.getItem(storeKey) || 'null');
      if (saved && saved.sessionId && Array.isArray(saved.transcript)) {
        setSessionId(saved.sessionId);
        setTranscript(saved.transcript);
        componentsRef.current = saved.components || {};
        setComponents(saved.components || {});
        return;                        // resumed — no new session
      }
    } catch { /* corrupted store — start fresh */ }
    begin();
  }, [cids.length]);  // eslint-disable-line react-hooks/exhaustive-deps

  // Persist after every settled turn so leaving the tab loses nothing.
  useEffect(() => {
    if (!sessionId || busy) return;
    try {
      sessionStorage.setItem(storeKey, JSON.stringify({ sessionId, transcript, components }));
    } catch { /* storage full — resume will just restart */ }
  }, [sessionId, transcript, components, busy, storeKey]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); },
    [transcript, components]);

  // emit: a component interaction re-enters the agent loop
  const emitFor = useCallback((cid) => (eventType, payload) => {
    if (!sessionId) return;
    if (eventType === 'chip_clicked' && payload && payload.id === 'new_session') {
      // Session closed after 10 graded questions — start a clean one.
      clearStored();
      setTranscript([]);
      setComponents({});
      componentsRef.current = {};
      setSessionId(null);
      begin();
      return;
    }
    if (eventType === 'answer_submitted') {
      setComponents((prev) => (prev[cid]
        ? { ...prev, [cid]: { ...prev[cid], status: 'submitted' } } : prev));
    }
    const inst = components[cid];
    const ev = makeEvent(eventType, { componentId: cid, questionId: inst?.question_id, payload });
    if (eventType === 'card_flipped') { sendEvent(sessionId, ev, {}); return; }   // telemetry, no turn
    run(() => sendEvent(sessionId, ev, { onFrame: handleFrame }));
  }, [sessionId, components, handleFrame, run, begin, clearStored]);

  // One input for everything: dictation lands in the same chat box.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const chunksRef = useRef([]);
  const toggleMic = useCallback(async () => {
    if (recording) { recRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false); setTranscribing(true);
        try {
          const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
          const b64 = await new Promise((res) => {
            const r = new FileReader();
            r.onloadend = () => res(String(r.result).split(',')[1]);
            r.readAsDataURL(blob);
          });
          const t = await APIService.transcribeAudio(b64);
          if (t && t.transcription) setInput((a) => (a ? `${a} ` : '') + t.transcription);
        } catch { /* ignore */ } finally { setTranscribing(false); }
      };
      recRef.current = rec; rec.start(); setRecording(true);
    } catch { /* mic unavailable */ }
  }, [recording]);

  const say = useCallback(() => {
    const text = input.trim();
    if (!text || busy || !sessionId) return;
    setInput('');
    setTranscript((prev) => [...prev, { kind: 'text', id: `u-${prev.length}`, md: text, user: true }]);
    run(() => sendSay(sessionId, text, { onFrame: handleFrame }));
  }, [input, busy, sessionId, handleFrame, run]);

  const renderInstance = (cid) => {
    const inst = components[cid];
    if (!inst) return null;
    const Comp = resolve(inst.component);
    return <Comp instance={{ ...inst, component_id: cid }} emit={emitFor(cid)} busy={busy} />;
  };

  if (!cids.length) {
    return <ModeEmpty title="No topics configured" body="Choose topics in Session Settings and your tutor will start a practice conversation." scene="theory" eyebrow="Guided understanding" onConfigure={onConfigure} />;
  }
  if (fatal) {
    return <ModeEmpty title="Tutor unavailable" body={fatal} scene="theory" eyebrow="Guided understanding" onConfigure={onConfigure} />;
  }

  const displayName = profile?.display_name || profile?.name || '';
  const initials = displayName.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  const subjects = (config?.subjects || []).map((x) => x.name).filter(Boolean);
  const title = subjects.slice(0, 2).join(' · ') + (subjects.length > 2 ? ` +${subjects.length - 2}` : '');

  return (
    <div className="pu-home pu-tutor">
      <div className="pu-tutor-top">
        <header className="pu-topbar">
          <HomeSearch entries={searchEntries}
            onPick={(e) => (e.kind === 'track' ? onOpenPath?.(e.id) : onOpenHubNode?.(e.id))} />
          <div className="pu-topbar-end">
            {onToggleTheme && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
            <button type="button" className="pu-avatar" onClick={onEditProfile}
              aria-label={displayName ? `${displayName} — profile` : 'Profile'}>
              {profile?.avatar ? <img src={profile.avatar} alt="" /> : <span>{(displayName || 'U').slice(0, 1).toUpperCase()}</span>}
            </button>
          </div>
        </header>
        {/* Compact header — a page heading, not a hero */}
        <section className="tt-head" aria-labelledby="tt-title">
          <div className="tt-head-main">
            {onBack && (
              <button type="button" className="pu-link tt-back" onClick={onBack}>
                <IconArrowLeft size={13} /> Back to Practice
              </button>
            )}
            <p className="pu-kicker">Theory</p>
            <h1 id="tt-title">{title || 'Theory practice'}</h1>
            <p className="tt-sub">Chat, explore and deepen your understanding with AI.</p>
          </div>
          <button type="button" className={`tt-learn${learnOpen ? ' on' : ''}`} onClick={toggleLearn} aria-pressed={learnOpen}
            title={learnOpen ? 'Hide the learn page' : 'Read the concept notes beside your practice'}>
            <IconBook size={15} /> Learn
          </button>
        </section>
      </div>

      <div className="tu-wrap">
        {learnOpen && (
          <LearnPanel conceptId={learnConcept || cids[0]} onClose={toggleLearn}
            conceptOptions={(config?.subjects || []).map((x) => ({ id: x.id, name: x.name }))}
            onPick={setLearnConcept} />
        )}
        <div className="tu-main">
          <div className="tu-transcript">
            {transcript.length === 0 && busy && (
              <div className="tu-thinking"><span /><span /><span /> Your tutor is preparing the first question…</div>
            )}
            {transcript.map((t, n) => {
              // One avatar per speaker turn: AI items that follow another AI item continue it.
              const prevT = transcript[n - 1];
              const cont = !!prevT && !t.user && !prevT.user;
              return t.kind === 'text' ? (
              <div key={t.id} className={`tu-msg${t.user ? ' user' : ''}${cont ? ' cont' : ''}`}>
                <span className="tu-avatar" aria-hidden="true">{t.user ? (initials || <IconPerson size={16} />) : '✦'}</span>
                <div className="tu-msgcol">
                  <div className="tu-msgbody">
                    {renderMarkdown(t.md)}
                    {t.streaming && <span className="tu-cursor" />}
                  </div>
                </div>
              </div>
            ) : (
              <div key={t.cid} className={`tu-slot-main${cont ? ' cont' : ''}`}>
                <span className="tu-avatar" aria-hidden="true">✦</span>
                {renderInstance(t.cid)}
              </div>
              );
            })}
            {busy && transcript.length > 0 && (
              <div className="tu-thinking small"><span /><span /><span /></div>
            )}
            <div ref={endRef} />
          </div>

          <div className="tu-composer">
            <textarea
              className="tu-composer-in"
              placeholder="Type your answer here, or ask a doubt… (Enter to send)"
              aria-label="Your answer or question"
              value={input}
              disabled={busy || !sessionId}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); say(); } }}
              rows={1}
            />
            <button type="button" className={`tu-mic${recording ? ' rec' : ''}`} onClick={toggleMic}
              disabled={transcribing || busy || !sessionId}
              aria-label={recording ? 'Stop recording' : 'Dictate your answer'}>
              {recording ? '■' : transcribing ? '…' : <IconMic size={16} />}
            </button>
            <button type="button" className="tu-primary tu-send" disabled={busy || !input.trim()} onClick={say}>
              Send
            </button>
          </div>
        </div>

        {toastId && components[toastId] && (
          <div className="tu-toast" role="status">
            {renderInstance(toastId)}
            <button type="button" className="tu-toast-x" onClick={() => setToastId(null)} aria-label="Dismiss">✕</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TutorMode;
